import { filterMenusBySpecialistGuidesFlag } from './specialistGuideMenuHelper';

export const FLOW_ENGINE_MENU_PATHS = new Set([
  '/admin-flow-title',
  '/admin-flow-module-config',
  '/admin-flow-ciiu-assignment',
  '/admin-flow-reassignment',
  '/admin-flow-checklist',
  '/specialist-flow-title',
  '/specialist-role-selection',
  '/specialist-manager-selection',
  '/specialist-validator-selection',
  '/specialist-editor-selection',
  '/specialist-pre-manager-selection',
  '/specialist-feeder',
  '/specialist-coordinator-selection',
  '/specialist-liaison-selection',
  '/specialist-flow-cmi',
  '/admin-flow-assignment-policies',
  '/admin-flow-coverage-groups'
]);

export const FLOW_MODULE_DEFINITIONS = {
  technology: { process: 'technology', moduleId: 1 },
  alternative: { process: 'alternative', moduleId: 2 },
  experience: { process: 'experience', moduleId: 3 },
  innovation: { process: 'innovation', moduleId: 4 },
  assistance: { process: 'assistance', moduleId: 5 },
  financial: { process: 'financial', moduleId: 6 },
  vision: { process: 'vision', moduleId: 7 },
  regulation: { process: 'regulation', moduleId: 8 }
};

/** Etiqueta i18n para el usuario. La clave interna (technology, …) no cambia. */
export const getModuleDisplayLabel = (process, t) => {
  const key = String(process || '').trim().toLowerCase();
  if (!key) {
    return '';
  }
  if (!FLOW_MODULE_DEFINITIONS[key] || typeof t !== 'function') {
    return process || key;
  }
  return t(`modules.${key}`, process || key);
};

const PROCESS_BY_MODULE_ID = Object.fromEntries(
  Object.entries(FLOW_MODULE_DEFINITIONS).map(([key, def]) => [Number(def.moduleId), key])
);

export const getModuleDisplayLabelById = (moduleId, t, fallback = '') => {
  const process = PROCESS_BY_MODULE_ID[Number(moduleId)];
  if (process) {
    return getModuleDisplayLabel(process, t);
  }
  return fallback || '';
};

export const getCatalogModuleOptionLabel = (mod, t) => {
  const fromKey = getModuleDisplayLabel(mod?.key || mod?.process, t);
  if (fromKey && fromKey !== String(mod?.key || mod?.process || '').toLowerCase()) {
    return fromKey;
  }
  return getModuleDisplayLabelById(mod?.id, t, mod?.name || mod?.description || '');
};

const isTruthy = (value) => String(value ?? '').trim().toLowerCase() === 'true';

export const isFlowEngineGloballyEnabled = (configurationState = {}) =>
  isTruthy(configurationState.flowEngineEnabled);

const MODULE_FLAG_KEYS = {
  technology: 'flowEngineTechnologyEnabled',
  alternative: 'flowEngineAlternativeEnabled',
  experience: 'flowEngineExperienceEnabled',
  innovation: 'flowEngineInnovationEnabled',
  assistance: 'flowEngineAssistanceEnabled',
  financial: 'flowEngineFinancialEnabled',
  vision: 'flowEngineVisionEnabled',
  regulation: 'flowEngineRegulationEnabled'
};

export const isFlowEngineEnabledForProcess = (configurationState = {}, process) => {
  if (!isFlowEngineGloballyEnabled(configurationState)) {
    return false;
  }
  const flagKey = MODULE_FLAG_KEYS[String(process || '').toLowerCase()];
  if (!flagKey) {
    return false;
  }
  return isTruthy(configurationState[flagKey]);
};

const LIST_TABLE_TRAY_VISUAL_FLAG_KEYS = {
  technology: 'listTableTrayVisualTechnologyEnabled',
  alternative: 'listTableTrayVisualAlternativeEnabled',
  experience: 'listTableTrayVisualExperienceEnabled',
  innovation: 'listTableTrayVisualInnovationEnabled',
  assistance: 'listTableTrayVisualAssistanceEnabled',
  financial: 'listTableTrayVisualFinancialEnabled',
  vision: 'listTableTrayVisualVisionEnabled',
  regulation: 'listTableTrayVisualRegulationEnabled'
};

/** List{Módulo}: true = chrome/columnas de bandeja; false = tabla dinámica actual. */
export const isListTableTrayVisualEnabledForProcess = (configurationState = {}, process) => {
  if (!isFlowEngineEnabledForProcess(configurationState, process)) {
    return false;
  }
  const flagKey = LIST_TABLE_TRAY_VISUAL_FLAG_KEYS[String(process || '').toLowerCase()];
  if (!flagKey) {
    return false;
  }
  const raw = configurationState[flagKey];
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return true;
  }
  return isTruthy(raw);
};

export const isModalReasonCatalogEnabled = (configurationState = {}) =>
  isTruthy(configurationState.flowEngineModalReasonIdEnabled);

export const isNoteForwardEnabled = (configurationState = {}) =>
  isTruthy(configurationState.flowEngineNoteForwardEnabled);

export const isExpertConsultEnabledForProcess = (configurationState = {}, process) => {
  if (!isFlowEngineGloballyEnabled(configurationState)) {
    return false;
  }
  if (!isTruthy(configurationState.flowEngineExpertConsultEnabled)) {
    return false;
  }
  const key = `flowEngineExpertConsult${String(process || '').charAt(0).toUpperCase()}${String(process || '').slice(1)}Enabled`;
  return isTruthy(configurationState[key]);
};

export const getChecklistLevel = (configurationState = {}) => {
  const raw = String(configurationState.flowChecklistLevel || 'subactivity').trim().toLowerCase();
  return raw === 'activity' ? 'activity' : 'subactivity';
};

const RECORD_EXCEL_FLAG_KEYS = {
  technology: 'recordUpdateExcelTechnology',
  alternative: 'recordUpdateExcelAlternative',
  experience: 'recordUpdateExcelExperience',
  innovation: 'recordUpdateExcelInnovation',
  assistance: 'recordUpdateExcelAssistance',
  financial: 'recordUpdateExcelFinancial',
  vision: 'recordUpdateExcelVision',
  regulation: 'recordUpdateExcelRegulation'
};

const INBOX_EDIT_SURFACE_FLAG_KEYS = {
  technology: 'flowInboxEditSurfacesTechnology',
  alternative: 'flowInboxEditSurfacesAlternative',
  experience: 'flowInboxEditSurfacesExperience',
  innovation: 'flowInboxEditSurfacesInnovation',
  assistance: 'flowInboxEditSurfacesAssistance',
  financial: 'flowInboxEditSurfacesFinancial',
  vision: 'flowInboxEditSurfacesVision',
  regulation: 'flowInboxEditSurfacesRegulation'
};

const INBOX_REASSIGN_SURFACE_FLAG_KEYS = {
  technology: 'flowInboxReassignSurfacesTechnology',
  alternative: 'flowInboxReassignSurfacesAlternative',
  experience: 'flowInboxReassignSurfacesExperience',
  innovation: 'flowInboxReassignSurfacesInnovation',
  assistance: 'flowInboxReassignSurfacesAssistance',
  financial: 'flowInboxReassignSurfacesFinancial',
  vision: 'flowInboxReassignSurfacesVision',
  regulation: 'flowInboxReassignSurfacesRegulation'
};

const INBOX_EDIT_SURFACES = ['pre-manager', 'coordinator', 'liaison', 'manager', 'validator', 'editor', 'cmi'];

const parsePipeList = (value = '') =>
  String(value || '')
    .split('|')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

export const isModuleConfigExcelImportEnabled = (configurationState = {}) =>
  isTruthy(configurationState.moduleConfigImportExcelEnabled);

export const isSupportInboxExcelExportEnabled = (configurationState = {}) => {
  const raw = configurationState.supportInboxExcelExportEnabled;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return true;
  }
  return isTruthy(raw);
};

export const isFlowHistoryExcelExportEnabled = (configurationState = {}) => {
  const raw = configurationState.flowHistoryExcelExportEnabled;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return true;
  }
  return isTruthy(raw);
};

const MASSIVE_DOWNLOAD_FLAG_KEYS = {
  feeder: 'massiveDownloadFeeder',
  'pre-manager': 'massiveDownloadPreManage',
  manager: 'massiveDownloadManager',
  validator: 'massiveDownloadValidator',
  editor: 'massiveDownloadEditor'
};

export const MASSIVE_DOWNLOAD_MODULES = 'technology|alternative|experience|innovation|financial|assistance|regulation|vision';

export const MASSIVE_DOWNLOAD_FORMATS = {
  feeder: ['term'],
  'pre-manager': ['term', 'dri'],
  manager: ['dri', 'prospecto'],
  validator: ['prospecto'],
  editor: ['prospecto']
};

export const isMassiveDownloadEnabledForModule = (configurationState = {}, surface = '', process = '') => {
  const flagKey = MASSIVE_DOWNLOAD_FLAG_KEYS[String(surface || '').trim().toLowerCase()];
  const moduleKey = String(process || '').trim().toLowerCase();
  if (!flagKey || !FLOW_MODULE_DEFINITIONS[moduleKey]) {
    return false;
  }
  const raw = configurationState[flagKey];
  if (raw === undefined || raw === null) {
    return true;
  }
  return String(raw)
    .split('|')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .includes(moduleKey);
};

export const isRecordExcelUpdateEnabledForProcess = (configurationState = {}, process, roleKey = '') => {
  const flagKey = RECORD_EXCEL_FLAG_KEYS[String(process || '').toLowerCase()];
  if (!flagKey) {
    return false;
  }
  const raw = String(configurationState[flagKey] || '').trim().toLowerCase();
  if (!raw) {
    return false;
  }
  const roles = raw.split('|').map((item) => item.trim()).filter(Boolean);
  return roles.includes(String(roleKey || '').trim().toLowerCase());
};

export const isInboxEditEnabledForProcess = (configurationState = {}, process, surface = '') => {
  if (!isFlowEngineGloballyEnabled(configurationState)) {
    return false;
  }
  const flagKey = INBOX_EDIT_SURFACE_FLAG_KEYS[String(process || '').toLowerCase()];
  const wanted = String(surface || '').trim().toLowerCase();
  if (!flagKey || !INBOX_EDIT_SURFACES.includes(wanted)) {
    return false;
  }
  return parsePipeList(configurationState[flagKey]).includes(wanted);
};

export const isInboxReassignEnabledForProcess = (configurationState = {}, process, surface = '') => {
  if (!isFlowEngineGloballyEnabled(configurationState)) {
    return false;
  }
  const flagKey = INBOX_REASSIGN_SURFACE_FLAG_KEYS[String(process || '').toLowerCase()];
  const wanted = String(surface || '').trim().toLowerCase();
  if (!flagKey || !INBOX_EDIT_SURFACES.includes(wanted)) {
    return false;
  }
  return parsePipeList(configurationState[flagKey]).includes(wanted);
};

export const isInboxAdminEditEnabled = (configurationState = {}) =>
  isFlowEngineGloballyEnabled(configurationState)
  && isTruthy(configurationState.flowInboxAdminEditEnabled ?? 'true');

export const isInboxAdminReassignEnabled = (configurationState = {}) =>
  isFlowEngineGloballyEnabled(configurationState)
  && isTruthy(configurationState.flowInboxAdminReassignEnabled ?? 'true');

export const resolveInboxSurfaceFromRoleName = (roleName = '', isAdmin = false) => {
  const lower = String(roleName || '').toLowerCase();
  if (lower.includes('feeder') || lower.includes('alimentador')) return 'feeder';
  if (lower.includes('pre-manager') || lower.includes('pre-gestor')) return 'pre-manager';
  if (lower.includes('coordinator') || lower.includes('coordinador')) return 'coordinator';
  if (lower.includes('liaison') || lower.includes('enlace')) return 'liaison';
  if (lower.includes('manager')) return 'manager';
  if (lower.includes('validator') || lower.includes('verificator') || lower.includes('verificador')) return 'validator';
  if (lower.includes('editor')) return 'editor';
  if (isAdmin) return 'cmi';
  return '';
};

export const isFeederGloballyEnabled = (configurationState = {}) =>
  isFlowEngineGloballyEnabled(configurationState);

export const isPreManagerGloballyEnabled = (configurationState = {}) =>
  isFlowEngineGloballyEnabled(configurationState)
  && isTruthy(configurationState.flowPreManagerEnabled ?? 'true');

export const isCoordinatorApprovalEnabled = (configurationState = {}) =>
  isPreManagerGloballyEnabled(configurationState)
  && isTruthy(configurationState.flowCoordinatorApprovalEnabled);

export const isLiaisonInboxEnabled = (configurationState = {}) =>
  isFlowEngineGloballyEnabled(configurationState)
  && isTruthy(configurationState.flowLiaisonInboxEnabled ?? 'true');

export const canInboxEdit = (configurationState = {}, process, surface = '', isAdmin = false) => {
  if (isAdmin && isInboxAdminEditEnabled(configurationState)) {
    return true;
  }
  return isInboxEditEnabledForProcess(configurationState, process, surface);
};

export const INBOX_REASSIGN_ROLES_DEFAULT = 'coordinator|liaison';

export const getInboxReassignRoles = (configurationState = {}) => {
  const raw = configurationState.flowInboxReassignRoles;
  if (raw == null || String(raw).trim() === '') {
    return parsePipeList(INBOX_REASSIGN_ROLES_DEFAULT);
  }
  return parsePipeList(raw);
};

export const canInboxReassign = (configurationState = {}, activeRoleKey = '') => {
  if (!isFlowEngineGloballyEnabled(configurationState)) {
    return false;
  }
  const wanted = String(activeRoleKey || '').trim().toLowerCase();
  if (!wanted) {
    return false;
  }
  return getInboxReassignRoles(configurationState).includes(wanted);
};

export const FLOW_POOL_SURFACES = ['feeder', 'pre-manager', 'manager', 'validator', 'editor', 'liaison', 'coordinator', 'panel', 'list'];
export const FLOW_POOL_SPECIALIST_ROLES = ['feeder', 'pre-manager', 'manager', 'validator', 'editor', 'liaison', 'coordinator'];
const FLOW_POOL_VIEWERS_DEFAULT = FLOW_POOL_SPECIALIST_ROLES.join('|');
const FLOW_POOL_OPERATOR_DEFAULTS = {
  feeder: 'feeder|coordinator',
  'pre-manager': 'pre-manager|coordinator',
  manager: 'manager|coordinator',
  validator: 'validator|coordinator',
  editor: 'editor|coordinator',
  liaison: 'liaison|coordinator',
  coordinator: 'coordinator',
  panel: FLOW_POOL_VIEWERS_DEFAULT,
  list: FLOW_POOL_VIEWERS_DEFAULT
};

const parsePoolPipeList = (value = '') =>
  String(value || '')
    .split('|')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

export const normalizePoolSurface = (surface = '') => {
  const wanted = String(surface || '').trim().toLowerCase();
  if (wanted === 'cmi') {
    return 'panel';
  }
  return FLOW_POOL_SURFACES.includes(wanted) ? wanted : '';
};

export const resolvePoolSurfaceFromPath = (path = '') => {
  const lower = String(path || '').toLowerCase();
  if (lower.includes('feeder') || lower.includes('alimentador')) return 'feeder';
  if (lower.includes('pre-manager')) return 'pre-manager';
  if (lower.includes('liaison')) return 'liaison';
  if (lower.includes('coordinator')) return 'coordinator';
  if (lower.includes('validator') || lower.includes('verificator') || lower.includes('verificador')) return 'validator';
  if (lower.includes('editor')) return 'editor';
  if (lower.includes('manager')) return 'manager';
  if (lower.includes('flow-cmi')) return 'panel';
  return 'list';
};

const poolConfigKey = (surface, kind) => {
  const normalized = normalizePoolSurface(surface);
  if (!normalized) {
    return '';
  }
  const suffix = kind === 'viewers' ? 'Viewers' : 'Operators';
  const camel = normalized === 'pre-manager'
    ? 'PreManager'
    : `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
  return `flowPool${suffix}${camel}`;
};

export const getPoolRoleList = (configurationState = {}, surface, kind = 'operators') => {
  const normalized = normalizePoolSurface(surface);
  if (!normalized) {
    return [];
  }
  const key = poolConfigKey(normalized, kind);
  const raw = configurationState[key];
  if (raw == null) {
    return parsePoolPipeList(
      kind === 'viewers' ? FLOW_POOL_VIEWERS_DEFAULT : FLOW_POOL_OPERATOR_DEFAULTS[normalized]
    );
  }
  return parsePoolPipeList(raw);
};

export const canPoolView = (configurationState = {}, surface, roleKey = '') => {
  const wanted = String(roleKey || '').trim().toLowerCase();
  if (!wanted) {
    return false;
  }
  return getPoolRoleList(configurationState, surface, 'viewers').includes(wanted);
};

export const canPoolOperate = (configurationState = {}, surface, roleKey = '') => {
  const wanted = String(roleKey || '').trim().toLowerCase();
  if (!wanted) {
    return false;
  }
  return getPoolRoleList(configurationState, surface, 'operators').includes(wanted);
};

export const resolveRecordExcelRoleKey = (configurationState = {}, process, roleKey = '', extraRoleKeys = []) => {
  const selected = String(roleKey || '').trim().toLowerCase();
  if (selected === 'pre-manager') {
    if (
      isRecordExcelUpdateEnabledForProcess(configurationState, process, 'pre-manager')
      || isRecordExcelUpdateEnabledForProcess(configurationState, process, 'manager')
    ) {
      return 'pre-manager';
    }
    return '';
  }
  if (selected && isRecordExcelUpdateEnabledForProcess(configurationState, process, selected)) {
    return selected;
  }
  const candidates = extraRoleKeys
    .map((item) => String(item || '').trim().toLowerCase())
    .filter((item) => item && item !== selected && item !== 'pre-manager');
  for (const key of candidates) {
    if (isRecordExcelUpdateEnabledForProcess(configurationState, process, key)) {
      return key;
    }
  }
  return '';
};

export const isChecklistEnabledForProcess = (configurationState = {}, process) => {
  if (!isFlowEngineGloballyEnabled(configurationState)) {
    return false;
  }
  if (!isTruthy(configurationState.flowChecklistEnabled)) {
    return false;
  }
  const key = `flowChecklist${String(process || '').charAt(0).toUpperCase()}${String(process || '').slice(1)}Enabled`;
  return isTruthy(configurationState[key]);
};

export const filterMenusByFlowEngineFlag = (menus = [], configurationState = {}) => {
  if (isFlowEngineGloballyEnabled(configurationState)) {
    return menus;
  }

  const filterTree = (items) => (items || [])
    .filter((item) => !FLOW_ENGINE_MENU_PATHS.has(item.path))
    .map((item) => ({
      ...item,
      submenu: filterTree(item.submenu)
    }));

  return filterTree(menus);
};

export const ENDUSER_ACTIVITY_MENU_PATH = '/end-user-activity';

export const isEndUserActivityReportsEnabled = (configurationState = {}) =>
  isTruthy(configurationState.enduserActivityReportsEnabled);

export const LIAISON_INBOX_MENU_PATH = '/specialist-liaison-selection';

export const filterMenusByLiaisonInboxFlag = (menus = [], configurationState = {}) => {
  if (isLiaisonInboxEnabled(configurationState)) {
    return menus;
  }

  const filterTree = (items) => (items || [])
    .filter((item) => item.path !== LIAISON_INBOX_MENU_PATH)
    .map((item) => ({
      ...item,
      submenu: filterTree(item.submenu)
    }));

  return filterTree(menus);
};

export const filterMenusByEndUserActivityFlag = (menus = [], configurationState = {}) => {
  if (isEndUserActivityReportsEnabled(configurationState)) {
    return menus;
  }

  const filterTree = (items) => (items || [])
    .filter((item) => item.path !== ENDUSER_ACTIVITY_MENU_PATH)
    .map((item) => ({
      ...item,
      submenu: filterTree(item.submenu)
    }));

  return filterTree(menus);
};

export const filterMenusByRuntimeFlags = (menus = [], configurationState = {}, userRoleState = {}) =>
  filterMenusBySpecialistGuidesFlag(
    filterMenusByLiaisonInboxFlag(
      filterMenusByEndUserActivityFlag(
        filterMenusByFlowEngineFlag(menus, configurationState),
        configurationState
      ),
      configurationState
    ),
    configurationState,
    userRoleState
  );

export const resolveFlowRoleContext = ({ userRole, authUser }) => {
  const selected = userRole?.roleLogin?.roleSpecialistSelected;
  const selectedKey = String(selected?.roleKey || selected?.name || selected?.code || '').toLowerCase();
  const isCoordinatorSelected = Boolean(selected?.id)
    && (selectedKey.includes('coordinator') || selectedKey.includes('coordinador'));

  if (isCoordinatorSelected) {
    return {
      roleId: selected.id,
      roleCode: 'coordinator',
      roleName: selected.name,
      isAdmin: true,
      canOperate: true,
      isViewerOnly: false
    };
  }

  if (selected?.id) {
    const roleCode = (selected.code || selected.name || '').toLowerCase();

    return {
      roleId: selected.id,
      roleCode,
      roleName: selected.name,
      isAdmin: false,
      canOperate: true,
      isViewerOnly: false
    };
  }

  return {
    roleId: null,
    roleCode: null,
    roleName: null,
    isAdmin: false,
    canOperate: false,
    userId: authUser?.user_id || authUser?.id
  };
};
